/**
 * RentalsPromoBlock.jsx
 * Created by Destin Frasier on 9/08/20.
 */

import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { useOffsetSize } from '../../../hooks';
import * as Icons from '../icons/Icons';
import { ExternalLink } from '../Link';
import FauxImage from '../FauxImage';

export default function RentalsPromoBlock({
    ariaLabel,
    buttonCopy,
    className,
    copy,
    href,
    isGearPromo,
    isInternalLink,
    isRvPromo,
    isTall,
    showArrow,
}) {

    const targetRef = useRef();
    const { height, width } = useOffsetSize(targetRef);
    const maxCopyWidth = 410;
    const maxCopyLength = 85

    const rvRentalPromoImage = "rv-rental-background";
    const gearRentalPromoImage = "gear-rental-background";

    let promoImageInventoryType = []
    if (isGearPromo){
        promoImageInventoryType = gearRentalPromoImage
    } else if (isRvPromo){
        promoImageInventoryType = rvRentalPromoImage
    }

    // Select a random background image
    const randomPromoImage = useRef(
        [rvRentalPromoImage, gearRentalPromoImage][Math.floor(Math.random() * 2)]
    );

    const arrow  = (
        <span className="rec-rentals-promo-single-icon-wrap">
            <Icons.IconArrowForward />
        </span>
    );

    const promoButtonCopy = () => {
        return (
            buttonCopy.length >= 1 ? (
                <span className="rec-rentals-promo-single-button-text">
                    &nbsp;{buttonCopy}
                    {arrow}
                </span>
            ) : (
                <span className="rec-rentals-promo-single-button-text">
                    {showArrow && (
                        arrow
                    )}
                </span>
            )
        );
    }

    const promoImage = () => {
        return (
            isGearPromo || isRvPromo ? (
            <FauxImage
                src={`/shared/img/global/rentals/${promoImageInventoryType}.jpg`}
                altText="Example"
                style={{ minHeight: `${height}px` }}
            />
            ) : (
                <FauxImage
                src={`/shared/img/global/rentals/${randomPromoImage.current}.jpg`}
                altText="Example"
                style={{ minHeight: `${height}px` }}
            />
            )
        );
    }

    return (
        <div
            ref={targetRef}
            className={cx(className, 'rec-rentals-promo-single-wrap')}
        >
            {/* Define whether the promo directs users to a Rec splash page or external link. */}
            {isInternalLink ? (
                <a
                    href={href}
                    aria-label={ariaLabel}
                    target="_blank"
                    rel="noopener noreferrer">
                    <div className={cx('rec-rentals-promo-single-inner-wrap', {
                        'rec-rentals-promo-single-tall' : isTall
                    })}>
                        <p className={cx({
                            'rec-rentals-promo-single-reduced-font-size' : copy.length >= maxCopyLength,
                            'rec-rentals-promo-single-set-max-text-width' : width >= maxCopyWidth
                        })}>
                            {copy}
                            {promoButtonCopy()}
                        </p>
                    </div>
                    {promoImage()}
                </a>
            ) : (
                <ExternalLink
                    url={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={ariaLabel}
                >
                    <div className={cx('rec-rentals-promo-single-inner-wrap', {
                        'rec-rentals-promo-single-tall' : isTall
                    })}>
                        <p className={cx({
                            'rec-rentals-promo-single-reduced-font-size' : copy.length >= maxCopyLength,
                            'rec-rentals-promo-single-set-max-text-width' : width >= maxCopyWidth
                        })}>
                            {copy}
                            {promoButtonCopy()}
                        </p>
                    </div>
                    {promoImage()}
                </ExternalLink>
            )}
        </div>
    );
}

RentalsPromoBlock.propTypes = {
    /** Aria-Label for the promo */
    ariaLabel: PropTypes.string,

    /** Bold copy displayed directly before the arrow */
    buttonCopy: PropTypes.string,

    /** Added to existing classes */
    className: PropTypes.string,
    
    /** Copy for the promo */
    copy: PropTypes.string,

    /** Url for the promo */
    href: PropTypes.string,

    // Display the Gear Rental background image */
    isGearPromo: PropTypes.bool,

    /** Does this promo direct users to a Rec splash page, or external site */
    isInternalLink: PropTypes.bool,

    // Display the RV Rental background image */
    isRvPromo: PropTypes.bool,

    // Increase the height of the promo
    isTall: PropTypes.bool,

    // Show or hide the arrow directly after the promo copy */
    showArrow: PropTypes.bool,
};

RentalsPromoBlock.defaultProps = {
    ariaLabel: 'Gear and RV rental options.',
    buttonCopy: '',
    className: undefined,
    copy: undefined,
    href: '/rentals',
    isGearPromo: undefined,
    isInternalLink: true,
    isRvPromo: undefined,
    isTall: false,
    showArrow: true,
};
